"use client";

import { useSpatiusAvatarContext } from "@/components/spatius-avatar/spatius-avatar-context";
import { cn } from "@/lib/utils";
import type { SpatiusAvatarConnectionStatus } from "@/types/spatius-avatar";
import { ConversationState as AvatarSdkConversationState } from "@spatius/avatarkit";
import type { HTMLAttributes } from "react";

const statusLabels: Record<SpatiusAvatarConnectionStatus, string> = {
  idle: "Tutor ready",
  initializing: "Getting tutor ready",
  connecting: "Getting tutor ready",
  connected: "Tutor ready",
  disconnecting: "Closing tutor video",
  error: "Tutor video unavailable",
};

const statusClasses: Record<SpatiusAvatarConnectionStatus, string> = {
  idle: "bg-secondary text-secondary-foreground",
  initializing: "bg-secondary text-secondary-foreground",
  connecting: "bg-secondary text-secondary-foreground",
  connected: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  disconnecting: "bg-secondary text-secondary-foreground",
  error: "bg-destructive/10 text-destructive",
};

export type SpatiusAvatarStatusProps = HTMLAttributes<HTMLDivElement>;

export function SpatiusAvatarStatus({ className, ...props }: SpatiusAvatarStatusProps) {
  const { conversationState, status } = useSpatiusAvatarContext();
  const statusLabel =
    status === "connected" && conversationState === AvatarSdkConversationState.playing
      ? "Tutor speaking"
      : statusLabels[status];

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold tracking-wide shadow-sm",
        statusClasses[status],
        className
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-2 rounded-full",
          status === "connected" && conversationState === AvatarSdkConversationState.playing
            ? "bg-emerald-500"
            : status === "connected"
              ? "bg-emerald-500"
              : status === "error"
                ? "bg-destructive"
                : "bg-current/60"
        )}
      />
      <span>{statusLabel}</span>
    </div>
  );
}
